import React from 'react';

import { Button, Card, CardBody, Link, Text } from '@change/ds-core-ui';

import { Translate, TranslatePlural, useI18n } from '@change-corgi/core/react/i18n';
import { ClientRender } from '@change-corgi/core/react/ssr/render';
import { VisuallyHidden } from '@change-corgi/design-system-legacy/a11y';
import { Loader } from '@change-corgi/design-system-legacy/components/progressiveDisclosure';

import { useLazyLoadedListFocus } from 'src/app/shared/hooks/focus';
import { capitalizeTopic } from 'src/app/shared/utils/topic';

import { InitialLetterFilter } from './components/InitialLetterFilter';
import { useFilterableTopics } from './hooks';

export const FilterableTopics: React.FC = () => {
	const {
		data: { topics, hasNextPage, nextPageLoading, totalCount },
		actions: { loadMore },
	} = useFilterableTopics();
	const { localizeNumber, translatePluralHtml } = useI18n();

	const { containerRef, loadMoreRef, loadingRef, getListItemRef, recordListEvent, lastEvent } =
		useLazyLoadedListFocus();

	return (
		<div
			ref={containerRef}
			data-testid="filterable-list-section"
			className="flex flex-col gap-8 text-typography-strong"
		>
			<InitialLetterFilter />
			<div className="space-y-6">
				<Text as="h2" variant="heading" size="small" fontWeight="light" data-qa="topic-sitemap-topics-count">
					<ClientRender
						placeholder={
							<Translate value="corgi.topic_page_sitemap.topic_list.title" replacements={{ count: totalCount }} />
						}
					>
						{() => (
							<Translate
								value="corgi.topic_page_sitemap.topic_list.title"
								replacements={{ count: localizeNumber(totalCount) }}
							/>
						)}
					</ClientRender>
				</Text>

				<div className="grid w-full gap-6 md:grid-cols-4">
					{topics.map((topic, index) => (
						<div key={topic.slug}>
							{lastEvent.type === 'loadMore' && index === lastEvent.firstNewItemIndex && (
								<VisuallyHidden role="alert">
									<TranslatePlural
										value="corgi.location_page_sitemap.location_list.fetched_more"
										count={topics.length - lastEvent.previousItemsCount}
										replacements={{ count: topics.length - lastEvent.previousItemsCount }}
									/>
								</VisuallyHidden>
							)}

							<Card
								ref={getListItemRef(index)}
								aria-label={topic.name}
								className="h-full w-full !rounded-xl [--card-padding:1.5rem] hover:elevation-low [&>span]:no-underline"
								as={Link}
								data-qa={`${topic.slug}-card`}
								href={`/topic/${encodeURIComponent(topic.slug)}?source_location=topic-sitemap`}
							>
								<CardBody className="[--card-padding:1.5rem]">
									<Text as="div" className="block font-bold underline">
										{capitalizeTopic(topic.name)}
									</Text>

									<Text as="div" className="mt-4 text-typography-strong" data-qa="topic-signatures-count">
										{translatePluralHtml(
											'corgi.topic_page_sitemap.topic_list.card_signatures_count',
											topic.signatureCount,
											{
												count: topic.signatureCountLocalized,
											},
										)}
									</Text>
								</CardBody>
							</Card>
						</div>
					))}
				</div>
			</div>

			<div className="self-center" data-qa="pagination-section">
				{nextPageLoading && (
					<>
						<VisuallyHidden role="alert">
							<Translate value="corgi.topic_page_sitemap.topic_list.fetching_more" />
						</VisuallyHidden>
						{/* TODO: tomato loading state */}
						<Loader size={44} mx="auto" ref={loadingRef} mt={24} />
					</>
				)}

				{!nextPageLoading && hasNextPage && (
					<Button
						size="small"
						variant="secondary"
						data-testid="explore-petitions-show-more-button"
						data-qa="explore-petitions-show-more-button"
						onClick={() => {
							recordListEvent({ type: 'loadMore', itemsCount: topics.length });
							loadMore();
						}}
						ref={loadMoreRef}
					>
						<Translate value="corgi.topic_page.petition_section.show_more" />
					</Button>
				)}
			</div>
		</div>
	);
};
